import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import mapValues from "lodash/mapValues";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { CircleSwitcherItem } from "@similarweb/ui-components";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import { transformData } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistributionTable.utils";
import type { IData } from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import type { dateForTooltip } from "app/services/DurationService";
import DurationServiceInstance from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

import {
    changeFilterItems,
    MAX_NUMBER_OF_ROWS,
    SERVER_API,
    TABLE_COLUMN_CLICKS_CHANGE,
    TABLE_COLUMN_KEYWORD,
    TABLE_COLUMN_SHARE,
    TABLE_COLUMN_VOLUME,
    TABLE_COLUMN_WINDOW_VOLUME,
    WIDGET_FOOTER_KEYWORDS_LINK_TEXT,
    WIDGET_TITLE_KEY,
    WIDGET_TOOLTIP_KEY,
} from "./const";
import { SwitcherWrapper, TableWrapper } from "./StyledComponents";
import { getColumns } from "./table/ColumnConfig";
import { getNoDataObject } from "./table/EmptyState";

const visibleColumns = [
    TABLE_COLUMN_KEYWORD,
    TABLE_COLUMN_SHARE,
    TABLE_COLUMN_CLICKS_CHANGE,
    TABLE_COLUMN_WINDOW_VOLUME,
    TABLE_COLUMN_VOLUME,
];

export const TopOrganicKeywords = () => {
    const { params } = useCurrentStateAndParams();
    const { duration, webSource, country, key, selectedPageTab, pageFilter, TopOrganicKeywordsFilter } = params;
    const changeIndex = changeFilterItems.findIndex((item) => item.id === TopOrganicKeywordsFilter);
    const [selectedChangeIndex, setSelectedChangeIndex] = useState(changeIndex > 0 ? changeIndex : 0);
    const durationData = DurationServiceInstance.getDurationData(duration);
    const { from, to, isWindow } = durationData.forAPI;

    const apiParams = {
        country,
        webSource,
        to,
        from,
        isWindow,
        keys: key,
        pageFilterJson: pageFilter,
        selectedPageTab: EKeywordTabs[EKeywordTabs.Organic],
        sourceType: EKeywordTabs[EKeywordTabs.Organic],
        ...(selectedChangeIndex > 0 && { Change: changeFilterItems[selectedChangeIndex]?.id }),
    };

    const paramsAsString = requestParams(apiParams);

    const { data, isLoading, isError } = useQuery({
        queryKey: [
            SERVER_API,
            to,
            from,
            duration,
            webSource,
            key,
            country,
            selectedPageTab,
            pageFilter,
            selectedChangeIndex,
            TopOrganicKeywordsFilter,
        ],
        queryFn: async () => DefaultFetchService.getInstance().post(`${SERVER_API}?${paramsAsString}`),
        select: (response: IData) => transformData(duration as string, MAX_NUMBER_OF_ROWS)(response),
    });

    const durationObject = DurationServiceInstance.getDurationData(duration as string);
    const durationTooltip = mapValues(durationObject.forTooltip as dateForTooltip, (v) => decodeURIComponent(v));
    const durationTooltipParams = {
        currentMonth: durationTooltip.to,
        lastMonth: durationTooltip.from,
    };

    const link = proNavigator.href("organicsearch_websites_v2_traffic_keywords", {
        key,
        mtd: false,
        webSource,
        selectedPageTab: EKeywordTabs[EKeywordTabs.Organic],
        country,
        duration,
        pageFilter,
        ...(selectedChangeIndex > 0 && { Change: changeFilterItems[selectedChangeIndex]?.id }),
    });

    const onKeywordClick = (value: string) => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", `table/${value}`);
    };

    const columns = getColumns(
        isWindow,
        visibleColumns,
        data?.TotalCount,
        onKeywordClick,
        durationTooltipParams,
    ) as ColumnsType<(typeof data.Data)[number]>;

    const footerCTA = {
        type: buttonTypes.FLAT,
        text: translate(WIDGET_FOOTER_KEYWORDS_LINK_TEXT),
        link,
        onClick: () => {
            allTrackers.dispatchTrackEvent("Internal Link", "click", "Competitive Opportunities/Top Organic Keywords");
        },
    };

    const handleChangeFilterSelection = (value: number) => {
        setSelectedChangeIndex(value);
        proNavigator.updateQueryParams({ topOrganicKeywordsFilter: changeFilterItems[value].id }, { reload: false });
    };

    return (
        <TableWrapper data-automation="top-organic-keywords">
            <WidgetContainer
                title={translate(WIDGET_TITLE_KEY)}
                tooltip={translate(WIDGET_TOOLTIP_KEY)}
                isLoading={false}
                loaderWidth={"100%"}
                loaderHeight={132}
                noData={false}
                withBorder={false}
                noDataTitle={translate("common.noresults")}
                noDataSubtitle={translate("keyword.analysis.overview.page.v2.trend_over_time.noData.subtitle")}
                footerButtonsConfig={[footerCTA]}
            >
                <>
                    <SwitcherWrapper
                        onItemClick={handleChangeFilterSelection}
                        selectedIndex={selectedChangeIndex}
                        customClass="CircleSwitcher"
                    >
                        {changeFilterItems.map((item, index) => (
                            <CircleSwitcherItem selected={selectedChangeIndex === index}>
                                {translate(item.text)}
                            </CircleSwitcherItem>
                        ))}
                    </SwitcherWrapper>
                    <Table<(typeof data.Data)[number]>
                        columnConfig={columns}
                        data={data?.Data ?? []}
                        isLoading={isLoading}
                        loaderRowCount={MAX_NUMBER_OF_ROWS}
                        noData={getNoDataObject(isError)}
                        sticky={false}
                        theme={"white"}
                        bordersConfig={{ header: false, body: false }}
                    />
                </>
            </WidgetContainer>
        </TableWrapper>
    );
};
